/**
 * Reports → Tasks subpage. Three daily shift report flows.
 *
 * Each task launches the typed report form (/reports/new/$type) so any
 * org member — including owners working in the field — can fill and submit.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Sunrise, Eye, Sunset, ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ReportType } from "@/lib/report-types";

export const Route = createFileRoute("/_authenticated/reports/tasks")({
  component: TasksPage,
  head: () => ({
    meta: [{ title: "Tasks — Reports — MissionReady" }],
  }),
});

type Task = { type: ReportType; title: string; subtitle: string; icon: LucideIcon };

const TASKS: Task[] = [
  {
    type: "shift_start",
    title: "Shift Start Report",
    subtitle: "Begin of shift documentation — post conditions, equipment check, briefing notes.",
    icon: Sunrise,
  },
  {
    type: "shift_observation",
    title: "Hourly Shift Observation Report",
    subtitle: "Log observations each hour on post — activity, anomalies, weather, foot traffic.",
    icon: Eye,
  },
  {
    type: "shift_close_out",
    title: "Shift Close-Out Report",
    subtitle: "End of shift summary and handoff notes for the relieving officer.",
    icon: Sunset,
  },
];

function TasksPage() {
  return (
    <div className="px-4 pb-28 pt-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-3xl">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground hover:text-foreground">
          <Link to="/reports">
            <ArrowLeft className="mr-1 h-4 w-4" /> Reports
          </Link>
        </Button>

        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Tasks</p>
        <h1 className="mt-1 font-display text-2xl font-bold md:text-3xl">Daily Shift Reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Routine documentation across your shift.
        </p>

        <ul className="mt-6 grid gap-3">
          {TASKS.map((t) => {
            const Icon = t.icon;
            return (
              <li key={t.type}>
                <Link
                  to="/reports/new/$type"
                  params={{ type: t.type }}
                  className="group flex items-start gap-4 rounded-2xl border border-[#C0C0C0]/15 bg-card p-4 shadow-card transition-all hover:border-primary/50 active:scale-[0.99]"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-inset ring-[#C0C0C0]/15">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base font-semibold text-foreground">{t.title}</div>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t.subtitle}</p>
                  </div>
                  <ChevronRight className="mt-2 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
